import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import quizService, {
  Quiz,
  QuizListResponse,
  CreateQuizData,
  UpdateQuizData,
  QuizTakeInfo,
  QuizAttempt,
  QuizResult,
  SubmitQuizData,
  UserQuizAttempts,
  AvailableQuestionsResponse
} from '../services/quizService';
import { Question } from '../services/questionService';

// Define the shape of quiz context state
interface QuizContextType {
  // Quiz Management State
  quizzes: Quiz[];
  currentQuiz: Quiz | null;
  quizLoading: boolean;
  quizError: string | null;

  // Quiz Taking State
  takingQuiz: QuizTakeInfo | null;
  currentAttempt: QuizAttempt | null;
  quizResults: QuizResult | null;
  takingLoading: boolean;
  takingError: string | null;

  // User Attempts State
  userAttempts: QuizAttempt[];
  attemptsLoading: boolean;
  attemptsError: string | null;

  // Available Questions State
  availableQuestions: Question[];
  questionsLoading: boolean;
  questionsError: string | null;

  // Pagination State
  pagination: {
    currentPage: number;
    lastPage: number;
    perPage: number;
    total: number;
  };

  // Quiz Management Actions
  fetchQuizzes: (page?: number, filters?: { search?: string; is_public?: boolean; per_page?: number }) => Promise<void>;
  fetchQuiz: (quizId: number) => Promise<Quiz | null>;
  createQuiz: (data: CreateQuizData) => Promise<Quiz | null>;
  updateQuiz: (quizId: number, data: UpdateQuizData) => Promise<Quiz | null>;
  deleteQuiz: (quizId: number) => Promise<boolean>;
  clearCurrentQuiz: () => void;

  // Quiz Taking Actions
  startTakingQuiz: (quizId: number, userInfo?: { name?: string; email?: string }) => Promise<QuizAttempt | null>;
  submitQuizAnswers: (quizId: number, data: SubmitQuizData) => Promise<QuizResult | null>;
  fetchQuizResults: (attemptId: number) => Promise<QuizResult | null>;
  clearTakingState: () => void;

  // Available Questions Actions
  fetchAvailableQuestions: (quizId?: number, filters?: any) => Promise<void>;
  clearAvailableQuestions: () => void;

  // User Attempts Actions
  fetchUserAttempts: (page?: number, filters?: { quiz_id?: number; per_page?: number }) => Promise<void>;
  clearUserAttempts: () => void;

  // Utility Actions
  clearAllErrors: () => void;
  setQuizError: (error: string | null) => void;
  setTakingError: (error: string | null) => void;
}

// Create the context with default values
const QuizContext = createContext<QuizContextType>({
  // Default state
  quizzes: [],
  currentQuiz: null,
  quizLoading: false,
  quizError: null,

  takingQuiz: null,
  currentAttempt: null,
  quizResults: null,
  takingLoading: false,
  takingError: null,

  userAttempts: [],
  attemptsLoading: false,
  attemptsError: null,

  availableQuestions: [],
  questionsLoading: false,
  questionsError: null,

  pagination: {
    currentPage: 1,
    lastPage: 1,
    perPage: 10,
    total: 0,
  },

  // Default functions
  fetchQuizzes: async () => { },
  fetchQuiz: async () => null,
  createQuiz: async () => null,
  updateQuiz: async () => null,
  deleteQuiz: async () => false,
  clearCurrentQuiz: () => { },

  startTakingQuiz: async () => null,
  submitQuizAnswers: async () => null,
  fetchQuizResults: async () => null,
  clearTakingState: () => { },

  fetchAvailableQuestions: async () => { },
  clearAvailableQuestions: () => { },

  fetchUserAttempts: async () => { },
  clearUserAttempts: () => { },

  clearAllErrors: () => { },
  setQuizError: () => { },
  setTakingError: () => { },
});

// Custom hook to use the quiz context
export const useQuiz = () => {
  const context = useContext(QuizContext);
  if (!context) {
    throw new Error('useQuiz must be used within a QuizProvider');
  }
  return context;
};

interface QuizProviderProps {
  children: ReactNode;
}

export const QuizProvider: React.FC<QuizProviderProps> = ({ children }) => {
  // Quiz Management State
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [currentQuiz, setCurrentQuiz] = useState<Quiz | null>(null);
  const [quizLoading, setQuizLoading] = useState(false);
  const [quizError, setQuizError] = useState<string | null>(null);

  // Quiz Taking State
  const [takingQuiz, setTakingQuiz] = useState<QuizTakeInfo | null>(null);
  const [currentAttempt, setCurrentAttempt] = useState<QuizAttempt | null>(null);
  const [quizResults, setQuizResults] = useState<QuizResult | null>(null);
  const [takingLoading, setTakingLoading] = useState(false);
  const [takingError, setTakingError] = useState<string | null>(null);

  // User Attempts State
  const [userAttempts, setUserAttempts] = useState<QuizAttempt[]>([]);
  const [attemptsLoading, setAttemptsLoading] = useState(false);
  const [attemptsError, setAttemptsError] = useState<string | null>(null);

  // Available Questions State
  const [availableQuestions, setAvailableQuestions] = useState<Question[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [questionsError, setQuestionsError] = useState<string | null>(null);

  // Pagination State
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    perPage: 10,
    total: 0,
  });

  // ============================================
  // QUIZ MANAGEMENT ACTIONS
  // ============================================

  const fetchQuizzes = useCallback(async (
    page: number = 1,
    filters?: { search?: string; is_public?: boolean; per_page?: number }
  ) => {
    setQuizLoading(true);
    setQuizError(null);

    try {
      const response: QuizListResponse = await quizService.getQuizzes(page, filters);
      setQuizzes(response.data.data);
      setPagination({
        currentPage: response.data.current_page,
        lastPage: response.data.last_page,
        perPage: response.data.per_page,
        total: response.data.total,
      });
    } catch (error: any) {
      const errorMessage = error?.message || 'Không thể tải danh sách bài tập';
      setQuizError(errorMessage);
      console.error('Error fetching quizzes:', error);
    } finally {
      setQuizLoading(false);
    }
  }, []);

  const fetchQuiz = useCallback(async (quizId: number): Promise<Quiz | null> => {
    setQuizLoading(true);
    setQuizError(null);

    try {
      const quiz = await quizService.getQuiz(quizId);
      setCurrentQuiz(quiz);
      return quiz;
    } catch (error: any) {
      const errorMessage = error?.message || 'Không thể tải thông tin bài tập';
      setQuizError(errorMessage);
      console.error('Error fetching quiz:', error);
      return null;
    } finally {
      setQuizLoading(false);
    }
  }, []);

  const createQuiz = useCallback(async (data: CreateQuizData): Promise<Quiz | null> => {
    setQuizLoading(true);
    setQuizError(null);

    try {
      const newQuiz = await quizService.createQuiz(data);
      setQuizzes(prev => [newQuiz, ...prev]);
      setCurrentQuiz(newQuiz);
      return newQuiz;
    } catch (error: any) {
      const errorMessage = error?.message || 'Không thể tạo bài tập mới';
      setQuizError(errorMessage);
      console.error('Error creating quiz:', error);
      return null;
    } finally {
      setQuizLoading(false);
    }
  }, []);

  const updateQuiz = useCallback(async (quizId: number, data: UpdateQuizData): Promise<Quiz | null> => {
    setQuizLoading(true);
    setQuizError(null);

    try {
      const updatedQuiz = await quizService.updateQuiz(quizId, data);

      // Update in quizzes list
      setQuizzes(prev => prev.map(quiz =>
        quiz.id === quizId ? updatedQuiz : quiz
      ));

      // Update current quiz if it's the same
      if (currentQuiz?.id === quizId) {
        setCurrentQuiz(updatedQuiz);
      }

      return updatedQuiz;
    } catch (error: any) {
      const errorMessage = error?.message || 'Không thể cập nhật bài tập';
      setQuizError(errorMessage);
      console.error('Error updating quiz:', error);
      return null;
    } finally {
      setQuizLoading(false);
    }
  }, [currentQuiz]);

  const deleteQuiz = useCallback(async (quizId: number): Promise<boolean> => {
    setQuizLoading(true);
    setQuizError(null);

    try {
      await quizService.deleteQuiz(quizId);

      // Remove from quizzes list
      setQuizzes(prev => prev.filter(quiz => quiz.id !== quizId));

      // Clear current quiz if it's the deleted one
      if (currentQuiz?.id === quizId) {
        setCurrentQuiz(null);
      }

      return true;
    } catch (error: any) {
      const errorMessage = error?.message || 'Không thể xóa bài tập';
      setQuizError(errorMessage);
      console.error('Error deleting quiz:', error);
      return false;
    } finally {
      setQuizLoading(false);
    }
  }, [currentQuiz]);

  const clearCurrentQuiz = useCallback(() => {
    setCurrentQuiz(null);
    setQuizError(null);
  }, []);

  // ============================================
  // QUIZ TAKING ACTIONS
  // ============================================

  const startTakingQuiz = useCallback(async (
    quizId: number,
    userInfo?: { name?: string; email?: string }
  ): Promise<QuizAttempt | null> => {
    console.log('startTakingQuiz called:', { quizId, userInfo });
    setTakingLoading(true);
    setTakingError(null);

    try {
      console.log('Getting quiz info...');
      // First get quiz info
      const quizInfo = await quizService.getQuizForTaking(quizId);
      console.log('Quiz info received:', quizInfo);
      setTakingQuiz(quizInfo);

      const attempt = await quizService.startQuiz(quizId, userInfo ? { user_info: userInfo } : undefined);
      console.log('Attempt created:', attempt);
      setCurrentAttempt(attempt);

      return attempt;
    } catch (error: any) {
      const errorMessage = error?.message || 'Không thể bắt đầu làm bài';
      console.error('Error in startTakingQuiz:', error);
      setTakingError(errorMessage);
      return null;
    } finally {
      setTakingLoading(false);
    }
  }, []);

  const submitQuizAnswers = useCallback(async (
    quizId: number,
    data: SubmitQuizData
  ): Promise<QuizResult | null> => {
    setTakingLoading(true);
    setTakingError(null);

    try {
      // Submit the quiz answers
      const submitResponse = await quizService.submitQuiz(quizId, data);

      // After successful submission, fetch the full results
      if (submitResponse?.data?.attempt_id) {
        const fullResults = await quizService.getQuizResults(submitResponse.data.attempt_id);
        setQuizResults(fullResults);
        return fullResults;
      } else {
        throw new Error('No attempt ID returned from submission');
      }
    } catch (error: any) {
      const errorMessage = error?.message || 'Không thể nộp bài';
      setTakingError(errorMessage);
      console.error('Error submitting quiz:', error);
      return null;
    } finally {
      setTakingLoading(false);
    }
  }, []);

  const fetchQuizResults = useCallback(async (attemptId: number): Promise<QuizResult | null> => {
    setTakingLoading(true);
    setTakingError(null);

    try {
      const results = await quizService.getQuizResults(attemptId);
      setQuizResults(results);
      return results;
    } catch (error: any) {
      const errorMessage = error?.message || 'Không thể tải kết quả bài tập';
      setTakingError(errorMessage);
      console.error('Error fetching quiz results:', error);
      return null;
    } finally {
      setTakingLoading(false);
    }
  }, []);

  const clearTakingState = useCallback(() => {
    setTakingQuiz(null);
    setCurrentAttempt(null);
    setQuizResults(null);
    setTakingError(null);
  }, []);

  // ============================================
  // AVAILABLE QUESTIONS ACTIONS
  // ============================================

  const fetchAvailableQuestions = useCallback(async (
    quizId?: number,
    filters?: any
  ) => {
    setQuestionsLoading(true);
    setQuestionsError(null);

    try {
      const response: AvailableQuestionsResponse = await quizService.getAvailableQuestions(quizId, filters);
      setAvailableQuestions(response.questions);
    } catch (error: any) {
      const errorMessage = error?.message || 'Không thể tải danh sách câu hỏi';
      setQuestionsError(errorMessage);
      console.error('Error fetching available questions:', error);
    } finally {
      setQuestionsLoading(false);
    }
  }, []);

  const clearAvailableQuestions = useCallback(() => {
    setAvailableQuestions([]);
    setQuestionsError(null);
  }, []);

  // ============================================
  // USER ATTEMPTS ACTIONS
  // ============================================

  const fetchUserAttempts = useCallback(async (
    page: number = 1,
    filters?: { quiz_id?: number; per_page?: number }
  ) => {
    setAttemptsLoading(true);
    setAttemptsError(null);

    try {
      const response: UserQuizAttempts = await quizService.getUserAttempts(page, filters);
      setUserAttempts(response.attempts);
      setPagination({
        currentPage: response.current_page,
        lastPage: response.last_page,
        perPage: response.per_page,
        total: response.total,
      });
    } catch (error: any) {
      const errorMessage = error?.message || 'Không thể tải lịch sử làm bài';
      setAttemptsError(errorMessage);
      console.error('Error fetching user attempts:', error);
    } finally {
      setAttemptsLoading(false);
    }
  }, []);

  const clearUserAttempts = useCallback(() => {
    setUserAttempts([]);
    setAttemptsError(null);
  }, []);

  // ============================================
  // UTILITY ACTIONS
  // ============================================

  const clearAllErrors = useCallback(() => {
    setQuizError(null);
    setTakingError(null);
    setAttemptsError(null);
    setQuestionsError(null);
  }, []);

  const setQuizErrorCallback = useCallback((error: string | null) => {
    setQuizError(error);
  }, []);

  const setTakingErrorCallback = useCallback((error: string | null) => {
    setTakingError(error);
  }, []);

  // Context value
  const contextValue: QuizContextType = {
    // State
    quizzes,
    currentQuiz,
    quizLoading,
    quizError,

    takingQuiz,
    currentAttempt,
    quizResults,
    takingLoading,
    takingError,

    userAttempts,
    attemptsLoading,
    attemptsError,

    availableQuestions,
    questionsLoading,
    questionsError,

    pagination,

    // Actions
    fetchQuizzes,
    fetchQuiz,
    createQuiz,
    updateQuiz,
    deleteQuiz,
    clearCurrentQuiz,

    startTakingQuiz,
    submitQuizAnswers,
    fetchQuizResults,
    clearTakingState,

    fetchAvailableQuestions,
    clearAvailableQuestions,

    fetchUserAttempts,
    clearUserAttempts,

    clearAllErrors,
    setQuizError: setQuizErrorCallback,
    setTakingError: setTakingErrorCallback,
  };

  return (
    <QuizContext.Provider value={contextValue}>
      {children}
    </QuizContext.Provider>
  );
};

export default QuizContext; 